import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export default function ProtectedRoute({ children, requireAdmin = false, requireSupervisor = false, requireScheduler = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EF]" data-testid="loading-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const role = user.role;
  const isAdmin = role === 'administrator';
  const isSupervisorOrAbove = isAdmin || role === 'supervisor';
  const isSchedulerOrAbove = isSupervisorOrAbove || role === 'scheduler';
  const isBoardDirector = role === 'board_director';
  const isHr = role === 'hr';

  // Board directors are restricted to their own portal + a tiny safe set
  // (plus any explicitly granted modules like Donor CRM / Marketing).
  const directorAllowed = ['/board', '/notifications', '/policies', '/eventadmin', '/event-live'];
  if (user.donor_crm_access) directorAllowed.push('/donors');
  if (user.marketing_access) directorAllowed.push('/marketing');
  if (isBoardDirector) {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (!directorAllowed.some(p => path === p || path.startsWith(p + '/'))) {
      return <Navigate to="/board" replace />;
    }
  }

  // HR users are siloed to the onboarding lifecycle
  // (plus any explicitly granted modules like Donor CRM / Marketing).
  const hrAllowed = ['/onboarding', '/notifications', '/policies', '/eventadmin', '/event-live'];
  if (user.donor_crm_access) hrAllowed.push('/donors');
  if (user.marketing_access) hrAllowed.push('/marketing');
  if (isHr) {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (!hrAllowed.some(p => path === p || path.startsWith(p + '/'))) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  const defaultLanding = isBoardDirector ? '/board' : isHr ? '/onboarding' : '/my-schedule';
  if (requireAdmin && !isAdmin) return <Navigate to={defaultLanding} replace />;
  if (requireSupervisor && !isSupervisorOrAbove) return <Navigate to={defaultLanding} replace />;
  if (requireScheduler && !isSchedulerOrAbove) return <Navigate to={defaultLanding} replace />;

  return children;
}
